/**
 * Pure presentation logic for the Vehicle DNA panel.
 *
 * Split out of `VehicleDnaPanel.tsx` so the two rules that actually carry
 * meaning — "is this vehicle's consumption acceptable?" and "what do we print
 * when the number is missing?" — are unit-testable without mounting React or
 * Recharts.
 *
 * Colour is never the only signal here: every toned bar is also labelled and
 * tooltipped, per the fleetGUARDIAN "colour + word, never colour alone" rule.
 */

import { SEMANTIC_FILLS } from '@/lib/chartTheme';
import { fmtR, fmtR2 } from '@/components/fleetguardian/format';

/** Printed wherever a benchmark is absent. Never render a bare null/NaN. */
export const EM_DASH = '—';

/**
 * How far above the OEM figure still counts as "amber" when there is no
 * cohort to compare against. Real vehicles never hit the factory number —
 * lab cycles are optimistic — so a flat 10 % grace band keeps the fallback
 * from painting the whole fleet red.
 */
export const OEM_TOLERANCE = 1.1;

export type DnaTone = 'good' | 'warn' | 'bad' | 'neutral';

/**
 * Tone → chart fill, sourced from the shared semantic chart tokens so the
 * panel matches severity encoding everywhere else in the SPA. No new hexes.
 */
export const TONE_FILL: Record<DnaTone, string> = {
  good: SEMANTIC_FILLS.severityLow, // soft green — at or under benchmark
  warn: SEMANTIC_FILLS.severityMedium, // amber — above median, inside p75
  bad: SEMANTIC_FILLS.severityHigh, // red — worse than the peer p75
  neutral: SEMANTIC_FILLS.operational, // blue — no benchmark to judge against
};

/** One-word gloss shown beside the actual bar so tone is never colour-only. */
export const TONE_WORD: Record<DnaTone, string> = {
  good: 'At or below peers',
  warn: 'Above peer median',
  bad: 'Worse than peer p75',
  neutral: 'No benchmark',
};

/**
 * Coerce a wire value to a usable number.
 *
 * Accepts numeric strings because Pydantic serialises `Decimal` as a JSON
 * string, so a column typed `NUMERIC` reaches the SPA as `"8.42"`. Rejects
 * null, undefined, NaN, Infinity, booleans, and blank/garbage strings —
 * every one of which would otherwise reach the DOM as "NaN" or "null".
 */
export function toNum(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null;
  if (typeof v === 'string') {
    const trimmed = v.trim();
    if (trimmed === '') return null;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/**
 * Colour state for the "Actual (90 days)" bar.
 *
 * Primary scale is the peer cohort, because peers already absorb terrain,
 * duty cycle, and load in a way the OEM lab figure cannot:
 *   * at or below the peer median → good
 *   * above the median but within p75 → warn
 *   * beyond p75 → bad
 *
 * When the cohort is missing (or has no median) it falls back to the OEM
 * figure with a `OEM_TOLERANCE` grace band. With neither benchmark present
 * there is nothing to judge against, so the bar stays neutral rather than
 * implying a verdict we cannot support.
 */
export function actualTone(
  actual: unknown,
  cohort: { median_l_per_100km?: unknown; p75_l_per_100km?: unknown } | null | undefined,
  oemLPer100: unknown,
): DnaTone {
  const value = toNum(actual);
  if (value === null) return 'neutral';

  const median = toNum(cohort?.median_l_per_100km);
  if (median !== null) {
    if (value <= median) return 'good';
    const p75 = toNum(cohort?.p75_l_per_100km);
    // Above the median with no upper bound known — warn, never escalate to red
    // on an assumption.
    if (p75 === null) return 'warn';
    return value > p75 ? 'bad' : 'warn';
  }

  const oem = toNum(oemLPer100);
  if (oem !== null && oem > 0) {
    if (value <= oem) return 'good';
    return value > oem * OEM_TOLERANCE ? 'bad' : 'warn';
  }

  return 'neutral';
}

/** `8.4 L/100km`, or an em-dash when the benchmark is absent. */
export function fmtLPer100(v: unknown): string {
  const n = toNum(v);
  return n === null ? EM_DASH : `${n.toFixed(1)} L/100km`;
}

/** `R 2.47/km`, or an em-dash. Cents matter at per-km scale, so 2 decimals. */
export function fmtRPerKm(v: unknown): string {
  const n = toNum(v);
  return n === null ? EM_DASH : `${fmtR2(n)}/km`;
}

/** `R 20.49/L`, or an em-dash. */
export function fmtRPerLitre(v: unknown): string {
  const n = toNum(v);
  return n === null ? EM_DASH : `${fmtR2(n)}/L`;
}

/** `12,345 km`, or an em-dash. */
export function fmtKm(v: unknown): string {
  const n = toNum(v);
  return n === null ? EM_DASH : `${new Intl.NumberFormat('en-ZA').format(Math.round(n))} km`;
}

/**
 * Excess-cost tile content. Overspend is the only state worth alarming on:
 * positive → red rands, at-or-below benchmark → a green `R 0` (we do not
 * pay fleet managers a rebate for beating the lab figure), absent → em-dash.
 */
export function excessCostDisplay(v: unknown): { text: string; tone: DnaTone } {
  const n = toNum(v);
  if (n === null) return { text: EM_DASH, tone: 'neutral' };
  if (n <= 0) return { text: 'R 0', tone: 'good' };
  return { text: fmtR(n), tone: 'bad' };
}
